import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GuideAccess = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [status, setStatus] = useState('loading'); // loading, valid, invalid
  const [guideId, setGuideId] = useState(null);

  useEffect(() => {
    const verifyAccess = async () => {
      try {
        const response = await axios.get(`${API}/guide-access/verify/${token}`);
        if (response.data.valid) {
          const gId = response.data.guide?.id;
          setGuideId(gId);
          setStatus('valid');
          
          // Send readable page view to Google Analytics
          if (window.gtag && gId) {
            const guideNames = {
              'iceland': 'Guida Islanda',
              'japan': 'Guida Giappone',
              'new-zealand': 'Guida Nuova Zelanda',
              'sicily': 'Guida Sicilia'
            };
            const guideName = guideNames[gId] || gId;
            
            // Page view with readable name
            window.gtag('event', 'page_view', {
              page_title: guideName,
              page_location: `${window.location.origin}/guide/${gId}`,
              page_path: `/guide/${gId}`
            });
            
            // Custom event for guide access tracking
            window.gtag('event', 'guide_accessed', {
              guide_id: gId,
              guide_name: guideName,
              access_type: 'post_purchase'
            });
          }
          
          // Redirect to the full guide after a brief success message
          setTimeout(() => {
            navigate(`/guide/${gId}?access=${token}`);
          }, 2000);
        } else {
          setStatus('invalid');
        }
      } catch (error) {
        console.error('Access verification error:', error);
        setStatus('invalid');
      }
    };
    
    if (token) {
      verifyAccess();
    } else {
      setStatus('invalid');
    }
  }, [token, navigate]);

  return (
    <main className="pt-20 min-h-screen bg-[#F5F5F7] flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl p-8 md:p-12 shadow-lg max-w-md w-full mx-4 text-center"
      >
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 text-[#C9B896] mx-auto mb-6 animate-spin" />
            <h1 className="font-['Manrope'] font-bold text-2xl text-[#1D1D1F] mb-2">
              {language === 'it' ? 'Verifica accesso...' : 'Verifying access...'}
            </h1>
            <p className="text-[#86868B]">
              {language === 'it' ? 'Attendere prego' : 'Please wait'}
            </p>
          </>
        )}

        {status === 'valid' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
            <h1 className="font-['Manrope'] font-bold text-2xl text-[#1D1D1F] mb-2">
              {language === 'it' ? 'Accesso Verificato!' : 'Access Verified!'}
            </h1>
            <p className="text-[#86868B]">
              {language === 'it' 
                ? 'Reindirizzamento alla guida...' 
                : 'Redirecting to your guide...'}
            </p>
          </>
        )}

        {status === 'invalid' && (
          <>
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h1 className="font-['Manrope'] font-bold text-2xl text-[#1D1D1F] mb-2">
              {language === 'it' ? 'Link Non Valido' : 'Invalid Link'}
            </h1>
            <p className="text-[#86868B] mb-6">
              {language === 'it' 
                ? 'Questo link di accesso non è valido o è scaduto.' 
                : 'This access link is invalid or has expired.'}
            </p>
            <button
              onClick={() => navigate('/guides')}
              className="text-[#C9B896] hover:underline font-medium"
            >
              {language === 'it' ? 'Vai alle Guide' : 'Go to Guides'}
            </button>
          </>
        )}
      </motion.div>
    </main>
  );
};

export default GuideAccess;
